import { useMutation } from '@apollo/react-hooks';
import React, { useRef } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { Upload, UploadVariables } from '../../../../generated/Upload';
import { UPLOAD } from '../../../../graphql/mutations';
import { colors, spacing, theme } from '../../constants/theme';
import { Button, Text } from '../../core-ui';
import { Props as TextProps } from '../../core-ui/Text';
import { getFileNameFromUrl } from '../../helpers';

export function UploadFieldInput({
  fileUrl,
  onFileSelected,
  onView,
  title,
  disabled,
  required,
  titleStyle,
  inputAccept = 'application/pdf,image/*',
  containerStyle,
}: {
  title?: string;
  fileUrl: string;
  onFileSelected?: (newImageUrl: string) => void;
  onView?: () => void;
  disabled?: boolean;
  required?: boolean;
  titleStyle?: TextProps;
  inputAccept?: string;
  containerStyle?: StyleProp<ViewStyle>;
}) {
  const [uploadFile, { loading: uploadLoading }] = useMutation<
    Upload,
    UploadVariables
  >(UPLOAD);

  let EditButton = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
      fileInputRef.current?.click(); // Programmatically click the hidden input
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const files = event.target.files;
      if (files && files.length === 1) {
        let file = files[0];
        if (file) {
          let response = await uploadFile({ variables: { file } });
          let newUrl = response.data?.upload.link;
          if (newUrl) {
            onFileSelected && onFileSelected(newUrl);
          }
        }
      }
    };

    return (
      <div>
        <Button
          title="Edit"
          onPress={handleButtonClick}
          style={styles.editAction}
          isLoading={uploadLoading}
        />
        <input
          accept={inputAccept}
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }} // Hide the input element
        />
      </div>
    );
  };

  return (
    <View style={containerStyle}>
      {!!title && (
        <Text>
          <Text {...titleStyle}>{title}</Text>
          {required && !Boolean(fileUrl) && (
            <Text bold size="m" style={[styles.lBottomSpacing, styles.red]}>
              *
            </Text>
          )}
        </Text>
      )}
      <View
        style={[
          styles.uploadContainer,
          disabled && {
            backgroundColor: colors.textInput.background.disabled,
          },
        ]}
      >
        <Text
          style={{
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {getFileNameFromUrl(fileUrl)}
        </Text>
        <View style={styles.actionBox}>
          {onFileSelected && !disabled && <EditButton />}
          <Button
            title="View"
            onPress={() => {
              window.open(fileUrl, '_blank');
              onView && onView();
            }}
            style={styles.viewAction}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  uploadContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: theme.colors.border.primary,
    backgroundColor: theme.colors.textInput.background.primary,
    paddingHorizontal: 8,
    paddingVertical: 12,
    marginTop: 12,
  },
  actionBox: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editAction: { marginRight: 8, height: '80%' },
  viewAction: { height: '80%' },
  red: { color: colors.text.red },
  lBottomSpacing: {
    marginBottom: spacing.l,
  },
});
